import React, { useState, FormEvent, useEffect, useRef, useCallback } from 'react';
import { 
  Button, 
  TextField, 
  Typography, 
  Grid, 
  FormControl,
  FormControlLabel, 
  Radio, 
  RadioGroup, 
  Box, 
  Paper,
  FormLabel,
  Alert,
  CircularProgress,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { 
  NewServiceRequestFormData,
  ServiceDetails,
  Attachment
} from 'shared-lib';
import { useServiceRequestQueries, useNotification } from 'shared-lib';
import { v4 as uuidv4 } from 'uuid';
import FileAttachmentUploader from './FileAttachmentUploader';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface NewServiceRequestFormProps {
  clientId: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  propertyData?: {
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  } | null;
}

interface ServiceField {
  type: 'number' | 'text';
  label: string;
  required?: boolean;
}

interface ServiceConfig {
  fields: {
    [key: string]: ServiceField;
  };
}

const NewServiceRequestForm: React.FC<NewServiceRequestFormProps> = ({ 
  clientId,
  onSuccess,
  onError,
  propertyData
}) => {
  const { useCreateServiceRequest } = useServiceRequestQueries();
  const createServiceRequest = useCreateServiceRequest();
  const { showNotification } = useNotification();
  
  // Generate a consistent requestId for all uploads in this form
  const requestIdRef = useRef(uuidv4());
  
  // Address fields
  const [address, setAddress] = useState(propertyData?.address || '');
  const [city, setCity] = useState(propertyData?.city || '');
  const [state, setState] = useState(propertyData?.state || '');
  const [zipCode, setZipCode] = useState(propertyData?.zipCode || '');
  
  // Service fields
  const [serviceType, setServiceType] = useState<'Residential' | 'Commercial' | 'Industrial'>('Residential');
  const [selectedServices, setSelectedServices] = useState<{[key: string]: boolean}>({});
  const [expandedServices, setExpandedServices] = useState<{[key: string]: boolean}>({});
  const [serviceFields, setServiceFields] = useState<{[key: string]: {[field: string]: string}}>({});
  const [touchedFields, setTouchedFields] = useState<{[key: string]: {[field: string]: boolean}}>({});
  const [timeline, setTimeline] = useState<'ASAP' | '1-2 weeks' | '2-3 weeks' | 'Preferred Date'>('ASAP');
  const [preferredDate, setPreferredDate] = useState<Date | null>(null);
  
  // Water location field
  const [waterLocation, setWaterLocation] = useState('');
  const [waterLocationTouched, setWaterLocationTouched] = useState(false);
  
  // Error handling
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState('');
  
  // Photos
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  
  // Service details as plain text
  const [serviceDescription, setServiceDescription] = useState('');
  
  // Upload status tracking
  const [uploadsInProgress, setUploadsInProgress] = useState(false);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  
  // Available services configuration
  const availableServices: {[key: string]: ServiceConfig} = {
    'Building Exteriors': {
      fields: {
        stories: { type: 'number', label: 'Stories', required: true },
        material: { type: 'text', label: 'Material', required: true },
        shape: { type: 'text', label: 'Shape (L-shape, square)', required: true },
        unitsPerBuilding: { type: 'number', label: 'Number of Units per building', required: true },
        quantity: { type: 'number', label: 'Quantity', required: true },
        otherDetails: { type: 'text', label: 'Other Details', required: false }
      }
    },
    'Interior Breezeways': {
      fields: {
        material: { type: 'text', label: 'Material', required: true },
        quantity: { type: 'number', label: 'Quantity', required: true },
        otherDetails: { type: 'text', label: 'Other Details', required: false }
      }
    },
    'Pool Area/Playground': {
      fields: {
        dimensions: { type: 'text', label: 'Dimensions', required: true },
        quantity: { type: 'number', label: 'Quantity', required: true },
        otherDetails: { type: 'text', label: 'Other Details', required: false }
      }
    },
    'Sidewalks': {
      fields: {
        numberOfPavers: { type: 'number', label: 'Number of Pavers', required: true },
        otherDetails: { type: 'text', label: 'Other Details', required: false }
      }
    },
    'Retaining Walls/Fences': {
      fields: {
        dimensions: { type: 'text', label: 'Dimensions', required: true },
        material: { type: 'text', label: 'Material', required: true },
        quantity: { type: 'number', label: 'Quantity', required: true },
        otherDetails: { type: 'text', label: 'Other Details', required: false }
      }
    },
    'Courtyard and Patios': {
      fields: {
        dimensions: { type: 'text', label: 'Dimensions', required: true },
        quantity: { type: 'number', label: 'Quantity', required: true },
        otherDetails: { type: 'text', label: 'Other Details', required: false }
      }
    },
    'Other': {
      fields: {
        dimensions: { type: 'text', label: 'Dimensions', required: true },
        material: { type: 'text', label: 'Material', required: true },
        description: { type: 'text', label: 'Description', required: true },
        otherDetails: { type: 'text', label: 'Other Details', required: false }
      }
    }
  };
  
  // Update address fields when propertyData changes
  useEffect(() => {
    if (propertyData) {
      if (propertyData.address) setAddress(propertyData.address);
      if (propertyData.city) setCity(propertyData.city);
      if (propertyData.state) setState(propertyData.state);
      if (propertyData.zipCode) setZipCode(propertyData.zipCode);
    }
  }, [propertyData]);
  
  // Handle file uploads
  const handleFilesUpload = useCallback((newAttachments: Attachment[]) => {
    // Always update attachments if we receive any
    if (Array.isArray(newAttachments)) {
      setAttachments(newAttachments);
    }
  }, []);
  
  // Handle upload status change
  const handleUploadStatusChange = useCallback((isUploading: boolean) => {
    setUploadsInProgress(isUploading);
    
    // If uploads just completed, mark as completed
    if (!isUploading && uploadsInProgress) {
      setUploadCompleted(true);
    }
  }, [uploadsInProgress]);
  
  // Handle service selection
  const handleServiceChange = (service: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setSelectedServices(prev => ({
      ...prev,
      [service]: isChecked
    }));
    
    // Update expanded state when service is selected/deselected
    setExpandedServices(prev => ({
      ...prev,
      [service]: isChecked
    }));
    
    // Initialize or clear service fields when toggling
    if (isChecked && !serviceFields[service]) {
      setServiceFields(prev => ({
        ...prev,
        [service]: {}
      }));
    } else if (!isChecked) {
      // Remove service fields when service is deselected
      setServiceFields(prev => {
        const newFields = { ...prev };
        delete newFields[service];
        return newFields;
      });
    }
  };
  
  // Handle accordion expansion
  const handleAccordionChange = (service: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedServices(prev => ({
      ...prev,
      [service]: isExpanded
    }));
    
    // If expanding, also select the service if not already selected
    if (isExpanded && !selectedServices[service]) {
      setSelectedServices(prev => ({
        ...prev,
        [service]: true
      }));
      
      // Initialize service fields if not already initialized
      if (!serviceFields[service]) {
        setServiceFields(prev => ({
          ...prev,
          [service]: {}
        }));
      }
    }
  };
  
  // Handle service field changes
  const handleFieldChange = (service: string, field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    // Update field value
    setServiceFields(prev => ({
      ...prev,
      [service]: {
        ...prev[service],
        [field]: event.target.value
      }
    }));
    
    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [service]: {
        ...(prev[service] || {}),
        [field]: true
      }
    }));
  };
  
  // Check if a field has been touched
  const isFieldTouched = (service: string, field: string) => {
    return touchedFields[service]?.[field] === true;
  };
  
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    
    // Reset error state
    setErrorMessage('');
    setShowError(false);
    
    // Check if uploads are still in progress
    if (uploadsInProgress) {
      const message = 'Please wait for all file uploads to complete before submitting';
      setErrorMessage(message);
      setShowError(true);
      return;
    }
    
    // Mark water location as touched
    setWaterLocationTouched(true);
    
    // Validate water location
    if (!waterLocation.trim()) {
      const message = 'Please specify the water location';
      setErrorMessage(message);
      setShowError(true);
      if (onError) onError(message);
      return;
    }
    
    // Get selected services
    const selectedServicesList = Object.entries(selectedServices)
      .filter(([_, isSelected]) => isSelected)
      .map(([service]) => service);
    
    // Validation check
    if (selectedServicesList.length === 0) {
      const message = 'Please select at least one service';
      setErrorMessage(message);
      setShowError(true);
      if (onError) onError(message);
      return;
    }

    // First, expand all selected services to ensure fields are visible
    const updatedExpandedServices = { ...expandedServices };
    selectedServicesList.forEach(service => {
      updatedExpandedServices[service] = true;
    });
    setExpandedServices(updatedExpandedServices);

    // Mark all fields as touched on submit
    const newTouchedFields = { ...touchedFields };
    selectedServicesList.forEach(service => {
      const serviceConfig = availableServices[service];
      newTouchedFields[service] = newTouchedFields[service] || {};
      
      Object.keys(serviceConfig.fields).forEach(fieldName => {
        newTouchedFields[service][fieldName] = true;
      });
    });
    setTouchedFields(newTouchedFields);

    // Track if we have validation errors
    let hasValidationErrors = false;
    let firstErrorService = '';

    // Validate required fields for selected services
    for (const service of selectedServicesList) {
      const serviceConfig = availableServices[service];
      const serviceData = serviceFields[service] || {};
      
      for (const [fieldName, fieldConfig] of Object.entries(serviceConfig.fields)) {
        if (fieldConfig.required && !serviceData[fieldName]) {
          const message = `Please fill in ${fieldConfig.label} for ${service}`;
          setErrorMessage(message);
          setShowError(true);
          if (onError) onError(message);
          hasValidationErrors = true;
          
          // Remember first service with error
          if (!firstErrorService) {
            firstErrorService = service;
          }
          
          // Don't return immediately, continue checking to expand all relevant accordions
        }
      }
    }
    
    // If validation errors, return without submitting
    if (hasValidationErrors) {
      return;
    }
    
    // Prepare service details 
    const serviceDetails: ServiceDetails = {
      description: JSON.stringify({
        services: serviceFields,
        waterLocation,
        serviceDescription
      }),
      attachments: attachments
    };
    
    // Combine service names for the primary service name
    // For "Other" service, use its description instead of just "Other"
    let combinedServiceName = selectedServicesList.map(service => {
      if (service === 'Other' && serviceFields['Other']?.description) {
        return `Other: ${serviceFields['Other'].description}`;
      }
      return service;
    }).join(', ');
    
    // Create form data object
    const formData: NewServiceRequestFormData = {
      city,
      state,
      address,
      zipCode,
      serviceType,
      projectTimeline: timeline,
      serviceName: combinedServiceName, // Use combined service names
      serviceDetails
    };
    
    // Add preferred date if specified
    if (timeline === 'Preferred Date' && preferredDate) {
      formData.preferredDate = preferredDate;
    }
    
    try {
      // Create the service request
      const requestId = await createServiceRequest.mutateAsync({
        clientId,
        formData
      });
      
      // Reset form
      setCity('');
      setState('');
      setAddress('');
      setZipCode('');
      setServiceType('Residential');
      setTimeline('ASAP');
      setPreferredDate(null);
      setSelectedServices({});
      setServiceFields({});
      setAttachments([]);
      setServiceDescription('');
      setWaterLocation('');
      setWaterLocationTouched(false);
      setUploadCompleted(false);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error creating service request:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to create service request. Please try again.';
      setErrorMessage(errorMsg);
      setShowError(true);
      if (onError) onError(errorMsg);
    }
  };
  
  // Show error notification when there's an error
  const prevErrorMessageRef = useRef<string | null>(null);
  useEffect(() => {
    if (errorMessage && errorMessage !== prevErrorMessageRef.current) {
      prevErrorMessageRef.current = errorMessage;
      showNotification({
        message: errorMessage,
        severity: 'error'
      });
    }
  }, [errorMessage, showNotification]);
  
  // Show photo upload error notification
  const prevPhotoErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (photoUploadError && photoUploadError !== prevPhotoErrorRef.current) {
      prevPhotoErrorRef.current = photoUploadError;
      showNotification({
        message: `File upload error: ${photoUploadError}`,
        severity: 'error'
      });
    }
  }, [photoUploadError, showNotification]);
  
  // Determine if form can be submitted
  const isSubmitDisabled = createServiceRequest.isPending || Object.keys(selectedServices).length === 0 || uploadsInProgress;
  
  return (
    <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: '16px' }}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>                    
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              label="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Grid>
          
          <Grid item xs={12} sm={8}>
            <TextField
              required
              fullWidth
              label="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              required
              fullWidth
              label="Zip Code"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend" sx={{ fontWeight: 500, color: '#101D3B', mb: 1 }}>Project Timeline</FormLabel>
              <RadioGroup
                row
                value={timeline}
                onChange={(e) => setTimeline(e.target.value as any)}
                sx={{ 
                  justifyContent: 'space-between',
                  '& .MuiFormControlLabel-root': { 
                    mr: { xs: 0, sm: 2 },
                    minWidth: { xs: '50%', sm: 'auto' }
                  }
                }}
              >
                <FormControlLabel value="ASAP" control={<Radio sx={{ color: '#00AB9B', '&.Mui-checked': { color: '#00AB9B' } }} />} label="ASAP" />
                <FormControlLabel value="1-2 weeks" control={<Radio sx={{ color: '#00AB9B', '&.Mui-checked': { color: '#00AB9B' } }} />} label="1-2 weeks" />
                <FormControlLabel value="2-3 weeks" control={<Radio sx={{ color: '#00AB9B', '&.Mui-checked': { color: '#00AB9B' } }} />} label="2-3 weeks" />
                <FormControlLabel value="Preferred Date" control={<Radio sx={{ color: '#00AB9B', '&.Mui-checked': { color: '#00AB9B' } }} />} label="Preferred Date" />
              </RadioGroup>
            </FormControl>
          </Grid>
          
          {timeline === 'Preferred Date' && (
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Preferred Service Date"
                  value={preferredDate}
                  onChange={(newDate) => setPreferredDate(newDate)}
                  sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </LocalizationProvider>
            </Grid>
          )}
          
          {/* Task Description Section */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography 
              sx={{ 
                fontWeight: 600, 
                fontSize: '1.2rem', 
                mb: 2,
                color: '#101D3B'
              }}
            >
              Task Description
            </Typography>
          </Grid>

          {/* Water Location Field */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Water Location"
              value={waterLocation}
              onChange={(e) => setWaterLocation(e.target.value)}
              onBlur={() => setWaterLocationTouched(true)}
              error={!waterLocation && waterLocationTouched}
              helperText={!waterLocation && waterLocationTouched ? 
                "Please specify where water is available on the property" : 
                "Specify the location of water access (e.g., outdoor spigot location, distance to water source)"
              }
              placeholder="E.g., 'Outdoor spigot on the east side of the house' or 'No water access available'"
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': { 
                  borderRadius: '8px',
                  backgroundColor: 'white'
                } 
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2,
                color: '#101D3B',
                fontWeight: 600
              }}
            >
              Select Services
            </Typography>
            <Box sx={{ mb: 3 }}>
              {Object.entries(availableServices).map(([service, config]) => (
                <Accordion 
                  key={service}
                  expanded={expandedServices[service] || false}
                  onChange={handleAccordionChange(service)}
                  sx={{
                    mb: 1,
                    '&:before': { display: 'none' },
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 171, 155, 0.2)',
                    boxShadow: 'none',
                    '&.Mui-expanded': {
                      backgroundColor: 'rgba(0, 171, 155, 0.03)'
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: selectedServices[service] 
                        ? 'rgba(0, 171, 155, 0.03)' 
                        : 'transparent'
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedServices[service] || false}
                          onChange={handleServiceChange(service)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            color: 'rgba(0, 171, 155, 0.6)',
                            '&.Mui-checked': {
                              color: '#00AB9B',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography 
                          sx={{ 
                            color: '#101D3B',
                            fontWeight: selectedServices[service] ? 600 : 400
                          }}
                        >
                          {service}
                        </Typography>
                      }
                      sx={{ m: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {Object.entries(config.fields).map(([fieldName, fieldConfig]) => (
                        <Grid item xs={fieldName === 'otherDetails' ? 12 : 12} sm={fieldName === 'otherDetails' ? 12 : 6} key={fieldName}>
                          <TextField
                            fullWidth
                            required={fieldConfig.required}
                            label={fieldConfig.label}
                            type={fieldConfig.type}
                            value={serviceFields[service]?.[fieldName] || ''}
                            onChange={handleFieldChange(service, fieldName)}
                            multiline={fieldName === 'otherDetails'}
                            rows={fieldName === 'otherDetails' ? 3 : undefined}
                            placeholder={fieldName === 'otherDetails' ? 'Add any additional details or special requirements' : undefined}
                            onBlur={() => {
                              setTouchedFields(prev => ({
                                ...prev,
                                [service]: {
                                  ...(prev[service] || {}),
                                  [fieldName]: true
                                }
                              }));
                            }}
                            InputProps={{
                              inputProps: { 
                                required: false,
                                min: fieldConfig.type === 'number' ? 0 : undefined,
                                step: fieldConfig.type === 'number' ? 1 : undefined
                              }
                            }}
                            error={fieldConfig.required && !serviceFields[service]?.[fieldName] && 
                                  selectedServices[service] && isFieldTouched(service, fieldName)}
                            helperText={fieldConfig.required && !serviceFields[service]?.[fieldName] && 
                                      selectedServices[service] && isFieldTouched(service, fieldName)
                              ? `${fieldConfig.label} is required` 
                              : ''}
                            sx={{ 
                              '& .MuiOutlinedInput-root': { 
                                borderRadius: '8px',
                                backgroundColor: 'white'
                              } 
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ 
              p: 3, 
              mt: 2,
              border: '1px solid rgba(0, 171, 155, 0.2)',
              borderRadius: '8px',
              backgroundColor: 'rgba(0, 171, 155, 0.03)'
            }}>
              <Typography 
                sx={{ 
                  fontWeight: 500, 
                  mb: 2,
                  color: '#101D3B'
                }}
              >
                Additional information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Additional information"
                    placeholder="Provide any additional information that might help us understand your requirements better. Include specific measurements, materials, or special considerations for your project."
                    value={serviceDescription}
                    onChange={(e) => setServiceDescription(e.target.value)}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: '8px',
                        backgroundColor: 'rgba(16, 29, 59, 0.02)'
                      } 
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <FileAttachmentUploader 
                      clientId={clientId}
                      requestId={requestIdRef.current}
                      onUploadComplete={handleFilesUpload}
                      onError={(error) => setPhotoUploadError(error.message)}
                      onUploadStatusChange={handleUploadStatusChange}
                      maxFiles={10}
                      autoUpdate={false}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          
          {/* Display warning if uploads are in progress */}
          {uploadsInProgress && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ borderRadius: '8px' }}>
                File uploads in progress. Please wait for all uploads to complete before submitting.
              </Alert>
            </Grid>
          )}
          
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              fullWidth
              disabled={isSubmitDisabled}
              sx={{ 
                py: 1.5, 
                borderRadius: '8px',
                backgroundColor: '#00AB9B',
                '&:hover': {
                  backgroundColor: '#008F85'
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(0, 171, 155, 0.5)'
                }
              }}
            >
              {createServiceRequest.isPending ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  Submitting...
                </>
              ) : uploadsInProgress ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  Uploading Files...
                </>
              ) : 'Submit Request'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default NewServiceRequestForm; 